import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DimensionCell, DimensionManifest, RunOutcome } from '../../../services/benchmarkService';
import { NotComputed } from '../BenchmarkPrimitives';
import { count } from '../BenchmarkVocabulary';
import { SIDE_LABELS, classLabel, dimensionNoun, isPositiveClass } from './investigationVocabulary';
import { dsVar } from '../../../theme/tokens';

export interface CellSelection {
    institution: string;
    platform: string;
}

interface DimensionMatrixProps {
    dimension: DimensionManifest;
    cells: DimensionCell[];
    selected?: CellSelection | null;
    /** A cell opens its cases below; selecting the selected cell again clears it. */
    onSelect?: (cell: CellSelection | null) => void;
    /** The outcomes each cell counts its cases against, as the run declares them. */
    outcomes?: RunOutcome[];
}

/** Each outcome a cell's cases met, in the run's words: "4 SAR filed · 9 confirmed suspicious". */
function outcomeLine(cell: DimensionCell, outcomes?: RunOutcome[]): string {
    if (outcomes?.length && cell.outcomes) {
        return outcomes.map((o) => `${count(cell.outcomes?.[o.key] ?? 0)} ${o.label.toLowerCase()}`).join(' · ');
    }
    return `${count(cell.truePositives)} confirmed · ${count(cell.sarsFiled)} SAR`;
}

/** Agreement colours: the same call, one class apart (ordinal), a different call. */
const CELL_ACCENT = { agree: '#10B981', adjacent: '#FBBF24', disagree: '#F97316' } as const;

const headSx = {
    px: 1.5, py: 1, fontSize: '0.6875rem', fontWeight: 600, color: dsVar('fg-muted'),
    textTransform: 'uppercase' as const, letterSpacing: '0.04em', whiteSpace: 'nowrap' as const
};

const ClassLabel: React.FC<{ dimension: DimensionManifest; code: string }> = ({ dimension, code }) => (
    <Box component="span" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: isPositiveClass(dimension, code) ? dsVar('accent-fg') : dsVar('fg-soft') }}>
        {classLabel(code, dimension)}
    </Box>
);

/**
 * Your team's decision against the platform's, for any dimension with classes.
 *
 * The flagging (or highest) class leads on both axes, so the top-left cell is where
 * both said yes and the off-diagonal cells are the disagreements a reviewer opens.
 * For an ordered dimension (an EDD rating) a cell one class off the diagonal is
 * marked as one step apart rather than as a plain disagreement. Each cell also says
 * how many of its cases met each outcome the run declares: a disagreement where the
 * case was reported is the one that matters, and counts alone cannot show it.
 */
const DimensionMatrix: React.FC<DimensionMatrixProps> = ({ dimension, cells, selected, onSelect, outcomes }) => {
    const axis = useMemo(() => [...dimension.classes].reverse(), [dimension.classes]);
    const byKey = useMemo(() => new Map(cells.map((c) => [`${c.institution}|${c.platform}`, c])), [cells]);
    const total = cells.reduce((sum, c) => sum + c.count, 0);

    if (!total) return <NotComputed detail={`No case could be compared on this ${dimensionNoun(dimension)}.`} compact />;

    const cell = (institution: string, platform: string) => byKey.get(`${institution}|${platform}`);
    const rowTotal = (institution: string) => axis.reduce((sum, p) => sum + (cell(institution, p)?.count ?? 0), 0);
    const columnTotal = (platform: string) => axis.reduce((sum, i) => sum + (cell(i, platform)?.count ?? 0), 0);

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: '6px', minWidth: 420 }} aria-label={`${dimension.label}: ${SIDE_LABELS.institution.toLowerCase()} against ${SIDE_LABELS.platform.toLowerCase()}`}>
                <Box component="thead">
                    <Box component="tr">
                        <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{SIDE_LABELS.institution} ↓ · {SIDE_LABELS.platform} →</Box>
                        {axis.map((p) => (
                            <Box key={p} component="th" sx={{ ...headSx, textAlign: 'center' }}>
                                <ClassLabel dimension={dimension} code={p} />
                            </Box>
                        ))}
                        <Box component="th" sx={{ ...headSx, textAlign: 'right' }}>Total</Box>
                    </Box>
                </Box>
                <Box component="tbody">
                    {axis.map((institution) => (
                        <Box key={institution} component="tr">
                            <Box component="th" scope="row" sx={{ ...headSx, textAlign: 'left' }}>
                                <ClassLabel dimension={dimension} code={institution} />
                            </Box>
                            {axis.map((platform) => {
                                const c = cell(institution, platform);
                                const n = c?.count ?? 0;
                                const kind = c?.agreement ?? (institution === platform ? 'agree' : 'disagree');
                                const isSelected = selected?.institution === institution && selected?.platform === platform;
                                const accent = CELL_ACCENT[kind] ?? CELL_ACCENT.disagree;
                                return (
                                    <Box
                                        key={platform}
                                        component="td"
                                        data-testid={`cell-${institution}-${platform}`}
                                        onClick={n && onSelect ? () => onSelect(isSelected ? null : { institution, platform }) : undefined}
                                        aria-pressed={onSelect && n ? isSelected : undefined}
                                        sx={{
                                            textAlign: 'center', px: 2.5, py: 1.5, minWidth: 140, borderRadius: '8px',
                                            cursor: n && onSelect ? 'pointer' : 'default',
                                            border: `1px solid ${isSelected ? dsVar('accent-fg') : n ? `${accent}55` : dsVar('line')}`,
                                            backgroundColor: isSelected ? 'rgba(255, 222, 89, 0.08)' : n ? `${accent}0F` : 'transparent',
                                            '&:hover': n && onSelect ? { borderColor: dsVar('accent-fg') } : undefined
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '1.125rem', fontWeight: n ? 800 : 400, color: n ? dsVar('fg-body') : dsVar('fg-faint'), fontVariantNumeric: 'tabular-nums' }}>
                                            {n ? count(n) : '·'}
                                        </Typography>
                                        {n > 0 && (
                                            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-2'), mt: 0.25 }}>
                                                {outcomeLine(c!, outcomes)}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                            <Box component="td" sx={{ textAlign: 'right', px: 1.5, color: dsVar('fg-2'), fontSize: '0.8125rem' }}>
                                {count(rowTotal(institution))}
                            </Box>
                        </Box>
                    ))}
                    <Box component="tr">
                        <Box component="th" scope="row" sx={{ ...headSx, textAlign: 'left' }}>Total</Box>
                        {axis.map((platform) => (
                            <Box key={platform} component="td" sx={{ textAlign: 'center', py: 0.5, color: dsVar('fg-2'), fontSize: '0.8125rem' }}>
                                {count(columnTotal(platform))}
                            </Box>
                        ))}
                        <Box component="td" sx={{ textAlign: 'right', px: 1.5, color: dsVar('fg-body'), fontSize: '0.8125rem', fontWeight: 700 }}>
                            {count(total)}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default DimensionMatrix;
